import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import ProgressStepper from '../ui/ProgressStepper'; 
import ServiceSelectionStep from './steps/ServiceSelectionStep';
import StepTwoServiceDetails from './steps/StepTwoServiceDetails';
import StepThreeYourInfo from './steps/StepThreeYourInfo';
import StepFourScheduling from './steps/StepFourScheduling';
import StepFiveReviewConfirm from './steps/StepFiveReviewConfirm';
import SubmissionSuccessView from './steps/SubmissionSuccessView';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/apiClient';

const TOTAL_STEPS = 5;

const compactText = (value) => (typeof value === 'string' ? value.trim() : '');

const ScheduleServiceModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessView, setShowSuccessView] = useState(false);
  const { user: authUser, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isOpen && !showSuccessView) {
      setCurrentStep(1);
      setFormData({});
      setIsSubmitting(false);
    } else if (!isOpen) {
      setShowSuccessView(false);
    }
  }, [isOpen, showSuccessView]);

  const handleNext = (stepData = {}) => {
    const nextFormData = { ...formData, ...stepData };
    setFormData(nextFormData);
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === TOTAL_STEPS) {
      handleSubmit(nextFormData);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepNumber) => {
    if (stepNumber >= 1 && stepNumber <= TOTAL_STEPS) {
      setCurrentStep(stepNumber);
    }
  };

  const handleSubmit = async (rawData) => {
    setIsSubmitting(true);
    console.log("Raw form data before transformation:", rawData);

    // Determine the userId for the service request
    let serviceRequestUserId = null;
    if (isAuthenticated && authUser && authUser.id) {
      serviceRequestUserId = authUser.id;
    } else if (rawData.guestUserId) {
      serviceRequestUserId = rawData.guestUserId;
    }

    const descriptionParts = [
      compactText(rawData.description),
      compactText(rawData.stepTwoDetails?.otherDescription)
    ].filter(Boolean);

    const normalizedPhone = rawData.stepThreeContactInfo?.phone
      ? rawData.stepThreeContactInfo.phone.replace(/\D/g, '')
      : rawData.stepThreeContactInfo?.phone;

    // Transform formData to match backend expectations
    const transformedData = {
      userId: serviceRequestUserId,
      contactFullName: rawData.stepThreeContactInfo?.fullName,
      contactPhone: normalizedPhone,
      contactEmail: rawData.stepThreeContactInfo?.email,
      contactStreet: rawData.stepThreeContactInfo?.streetAddress,
      contactCity: rawData.stepThreeContactInfo?.city,
      contactZip: rawData.stepThreeContactInfo?.zipCode,
      serviceType: rawData.serviceType,
      description: descriptionParts.length > 0 ? descriptionParts.join('\n\n') : rawData.serviceType,
      schedulingPreferences: {
        preferredDate1: rawData.stepFourSchedule?.preferredDate1,
        preferredTime1: rawData.stepFourSchedule?.preferredTime1,
        preferredDate2: rawData.stepFourSchedule?.preferredDate2,
        preferredTime2: rawData.stepFourSchedule?.preferredTime2,
        notes: rawData.stepFourSchedule?.schedulingNotes,
      },
      additionalDetails: {
        commonIssues: rawData.stepTwoDetails?.commonIssues,
        systemAge: rawData.stepTwoDetails?.systemAge,
        systemType: rawData.stepTwoDetails?.systemType,
        installationType: rawData.stepTwoDetails?.installationType,
        propertySize: rawData.stepTwoDetails?.propertySize,
        propertyType: rawData.stepTwoDetails?.propertyType,
        selectedTier: rawData.stepTwoDetails?.selectedTier,
        addOns: rawData.stepTwoDetails?.addOns,
        generalNotes: rawData.stepTwoDetails?.generalNotes,
      }
    };

    console.log("Submitting transformed data:", transformedData);

    try {
      const response = await apiClient.post('/service-requests', transformedData);
      
      console.log("Form submitted successfully!", response.data);
      setShowSuccessView(true); 
    } catch (error) {
      console.error("Error submitting form:", error);
      
      let errorMessage = 'There was an error submitting your request. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (showSuccessView) {
      return <SubmissionSuccessView formData={formData} onClose={onClose} />;
    }
    switch (currentStep) {
      case 1:
        return <ServiceSelectionStep onNext={handleNext} formData={formData} />;
      case 2:
        return <StepTwoServiceDetails onNext={handleNext} onPrevious={handlePrevious} formData={formData} />;
      case 3:
        return <StepThreeYourInfo onNext={handleNext} onPrevious={handlePrevious} formData={formData} />;
      case 4:
        return <StepFourScheduling onNext={handleNext} onPrevious={handlePrevious} formData={formData} />;
      case 5:
        return <StepFiveReviewConfirm onNext={handleNext} onPrevious={handlePrevious} formData={formData} goToStep={goToStep} isSubmitting={isSubmitting} />;
      default:
        return null;
    }
  };

  const modalTitle = showSuccessView ? 
    (formData.stepThreeContactInfo?.fullName ? `Thank You, ${formData.stepThreeContactInfo.fullName}!` : "Request Confirmed!") 
    : "Schedule Service with Quality Air of Texas";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h2 className="text-xl md:text-2xl font-semibold text-brand-text-dark">{modalTitle}</h2>
          {!showSuccessView && (
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              aria-label="Close modal"
            >
              <XMarkIcon className="h-7 w-7" />
            </button>
          )}
        </div>

        {/* Modal Body */} 
        <div className="p-6 space-y-6 overflow-y-auto flex-grow scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {!showSuccessView && (
            <div className="mb-6">
              <ProgressStepper currentStep={currentStep} totalSteps={TOTAL_STEPS} />
            </div>
          )}
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default ScheduleServiceModal;